import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../sectepe-ui/index';
import leadSyncService, { Lead, LemlistCampaign } from '../services/leadSyncService';
import { PageHeader, EmptyState, LoadingState, useConfirm, useFocusParam } from '../components/ui';
import { useKampagnenZuordnung, kampagnenTitel, KampagneVerweis } from '../vertrieb/kampagnen';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { useUrlParams } from '../vertrieb/useUrlParams';
import { fehlerText } from '../vertrieb/format';
import { LEAD_QUELLE, LEAD_STATUS, leadName } from '../vertrieb/leads/leadStatus';
import { LeadTabelle } from '../vertrieb/leads/LeadTabelle';
import { LeadFormular } from '../vertrieb/leads/LeadFormular';
import { ImportDialog } from '../vertrieb/leads/ImportDialog';
import { AnreicherungPanel } from '../vertrieb/leads/AnreicherungPanel';
import { IcebreakerPanel } from '../vertrieb/leads/IcebreakerPanel';
import { KampagnenPanel } from '../vertrieb/leads/KampagnenPanel';
import { DurchlaufPanel } from '../vertrieb/leads/DurchlaufPanel';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import type { Recht } from '../berechtigungen';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

/**
 * Leads & Outreach, Schritt 2 im Vertrieb.
 *
 * Eine Liste, fuenf Arbeitsbereiche: Leads pflegen, mit Clay anreichern,
 * Icebreaker erzeugen, in Lemlist-Kampagnen aufnehmen, beides in einem Lauf. Die Auswahl in
 * der Liste gilt fuer alle Bereiche. Bereich und Filter stehen in der URL.
 *
 * Rechte (Paket 2): Clay-Anreicherung und Massenimport brauchen
 * vertrieb.loeschen. Der Lemlist-Bereich (Nachrichten-Vorschau, Kampagnenstand)
 * braucht versand.lesen, wie im Backend; das Aufnehmen in und Entfernen aus
 * Kampagnen sowie der Durchlauf brauchen versand.ausfuehren. Ohne Recht
 * fehlen Bereich und Knopf; ein ?tab= ohne Recht faellt auf die Liste
 * zurueck, und Kennzahlen verlinken nur auf Bereiche, die es fuer die
 * Person gibt.
 *
 * Welle 2: Anlegen, Bearbeiten und Icebreaker erzeugen verlangen
 * vertrieb.bearbeiten (Buchhaltung liest nur); GET /lemlist/campaigns liegt im
 * Versand-Router und wird nur mit versand.lesen geladen.
 */

type Bereich = 'leads' | 'clay' | 'icebreaker' | 'lemlist' | 'durchlauf';

const BEREICHE: { id: Bereich; label: string; recht?: Recht }[] = [
  { id: 'leads', label: 'Leads' },
  { id: 'clay', label: 'Anreichern (Clay)', recht: 'vertrieb.loeschen' },
  { id: 'icebreaker', label: 'Icebreaker', recht: 'vertrieb.bearbeiten' },
  { id: 'lemlist', label: 'Kampagnen (Lemlist)', recht: 'versand.lesen' },
  { id: 'durchlauf', label: 'Anreichern & senden', recht: 'versand.ausfuehren' },
];

const LeadSyncPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { get, set } = useUrlParams();
  const { darf } = useBerechtigung();
  const darfImportieren = darf('vertrieb.loeschen');
  const darfSenden = darf('versand.ausfuehren');
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const siehtKampagnen = darf('versand.lesen');
  const bereiche = BEREICHE.filter((b) => !b.recht || darf(b.recht));
  const hatBereich = (id: Bereich) => bereiche.some((b) => b.id === id);
  const bereich: Bereich = bereiche.find((b) => b.id === get('tab'))?.id ?? 'leads';
  const status = get('status');
  const quelle = get('quelle');
  const suche = get('suche');
  // ?kampagne=<Lemlist-ID>: Sprung von ICP-, Signal-Kampagne oder Sequenz.
  const kampagne = get('kampagne');
  const [sucheEingabe, setSucheEingabe] = useState(suche);
  const zuordnung = useKampagnenZuordnung();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ausgewaehlt, setAusgewaehlt] = useState<Set<string>>(new Set());

  const [kampagnen, setKampagnen] = useState<LemlistCampaign[]>([]);
  const [laedtKampagnen, setLaedtKampagnen] = useState(false);
  const [kampagnenGeladen, setKampagnenGeladen] = useState(false);

  const [zeigeFormular, setZeigeFormular] = useState(false);
  const [bearbeite, setBearbeite] = useState<Lead | null>(null);
  const [zeigeImport, setZeigeImport] = useState(false);

  // Suche entprellt in die URL, sonst filtert jeder Tastendruck neu.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (sucheEingabe.trim() !== suche) set('suche', sucheEingabe.trim(), { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [sucheEingabe, suche, set]);

  const laden = useCallback(async () => {
    try {
      const daten = await leadSyncService.getLeads({
        status: status || undefined,
        source: quelle || undefined,
      });
      setLeads(daten ?? []);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Leads konnten nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [status, quelle]);

  useEffect(() => { laden(); }, [laden]);

  const kampagnenLaden = useCallback(async () => {
    if (!siehtKampagnen) return;
    setLaedtKampagnen(true);
    try {
      setKampagnen((await leadSyncService.getLemlistCampaigns()) ?? []);
      setKampagnenGeladen(true);
    } catch (e) {
      setFehler(fehlerText(e, 'Lemlist-Kampagnen konnten nicht geladen werden'));
    } finally {
      setLaedtKampagnen(false);
    }
  }, [siehtKampagnen]);

  useEffect(() => {
    if (siehtKampagnen && (bereich === 'lemlist' || bereich === 'durchlauf') && !kampagnenGeladen && !laedtKampagnen) kampagnenLaden();
  }, [siehtKampagnen, bereich, kampagnenGeladen, laedtKampagnen, kampagnenLaden]);

  // Auswahl bereinigen, wenn Leads verschwinden (geloescht, Filter).
  useEffect(() => {
    setAusgewaehlt((alt) => {
      const vorhanden = new Set(leads.map((l) => l.id));
      const next = new Set([...alt].filter((id) => vorhanden.has(id)));
      return next.size === alt.size ? alt : next;
    });
  }, [leads]);

  const sichtbar = useMemo(() => {
    const begriff = suche.toLowerCase();
    const inKampagne = kampagne ? leads.filter((l) => l.lemlistCampaignId === kampagne) : leads;
    if (!begriff) return inKampagne;
    return inKampagne.filter((l) => [l.email, l.firstName, l.lastName, l.company, l.jobTitle]
      .some((w) => (w || '').toLowerCase().includes(begriff)));
  }, [leads, suche, kampagne]);

  // ?lead=<id>: zur Zeile springen (Firmenakte, Antworten, ICP-Kampagne).
  useFocusParam('lead', !laedt && bereich === 'leads');

  const ausgewaehltIds = useMemo(() => [...ausgewaehlt], [ausgewaehlt]);
  const kampagnenName = useCallback(
    (id: string) => kampagnenTitel(id, zuordnung, kampagnen.find((k) => k._id === id)?.name),
    [kampagnen, zuordnung],
  );

  const umschalten = (id: string) => setAusgewaehlt((alt) => {
    const next = new Set(alt);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const alleUmschalten = () => setAusgewaehlt((alt) => (
    sichtbar.length > 0 && sichtbar.every((l) => alt.has(l.id)) ? new Set() : new Set(sichtbar.map((l) => l.id))
  ));

  const loeschen = async (lead: Lead) => {
    const ok = await confirm({
      title: 'Lead löschen?',
      message: `${leadName(lead)} wird aus Intermal entfernt. In Lemlist bleibt der Kontakt, bis er dort ausgetragen wird.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await leadSyncService.deleteLead(lead.id);
      push({ message: `${leadName(lead)} gelöscht.`, severity: 'info' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const ausKampagne = async (lead: Lead) => {
    const ok = await confirm({
      title: 'Aus der Lemlist-Kampagne entfernen?',
      message: `${leadName(lead)} bekommt keine weiteren Nachrichten aus „${kampagnenName(lead.lemlistCampaignId || '')}“.`,
      confirmLabel: 'Entfernen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await leadSyncService.removeFromLemlist(lead.id);
      push({ message: `${leadName(lead)} aus der Kampagne entfernt.`, severity: 'info' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Entfernen fehlgeschlagen'));
    }
  };

  const zaehle = (st: string) => leads.filter((l) => l.status === st).length;
  const inKampagne = leads.filter((l) => l.lemlistCampaignId).length;
  const ohneFirma = leads.filter((l) => !l.companyId).length;
  const gefiltert = Boolean(status || quelle || suche || kampagne);
  const filterZuruecksetzen = () => {
    setSucheEingabe('');
    set('status', null, { replace: true });
    set('quelle', null, { replace: true });
    set('suche', null, { replace: true });
    set('kampagne', null, { replace: true });
  };

  if (laedt) {
    return (
      <div className="page-container-wide">
        <SalesProcessBar stepId="outreach" />
        <LoadingState rows={5} label="Leads werden geladen" />
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <SalesProcessBar stepId="outreach" />
      <PageHeader
        title="Leads & Outreach"
        subtitle="Ansprechpartner sammeln, mit Clay anreichern und in Lemlist-Kampagnen aufnehmen. Was zurückkommt, landet unter Antworten."
        actions={(
          <>
            {darfImportieren && (
              <button type="button" className="btn btn-secondary" onClick={() => setZeigeImport(true)}>
                <Icon name="archive" size={14} /> Importieren
              </button>
            )}
            {darfBearbeiten && (
              <button type="button" className="btn btn-primary" onClick={() => { setBearbeite(null); setZeigeFormular(true); }}>
                <Icon name="plus" size={14} /> Neuer Lead
              </button>
            )}
          </>
        )}
      />
      <RechteHinweis was="Leads anlegen und bearbeiten" />

      {leads.length > 0 && (
        <KpiRow
          aria-label="Lead-Kennzahlen"
          items={[
            { label: 'Leads', value: leads.length },
            { label: 'neu', value: zaehle('new'), to: '/lead-sync?status=new' },
            { label: 'angereichert', value: leads.filter((l) => l.clayEnrichedAt).length, to: hatBereich('clay') ? '/lead-sync?tab=clay' : undefined },
            { label: 'mit Icebreaker', value: leads.filter((l) => l.icebreakerGeneratedAt).length, to: hatBereich('icebreaker') ? '/lead-sync?tab=icebreaker' : undefined },
            { label: 'in Kampagne', value: inKampagne, tone: inKampagne ? 'good' : 'neutral', to: hatBereich('lemlist') ? '/lead-sync?tab=lemlist' : undefined },
            { label: 'ohne Firmenakte', value: ohneFirma, tone: ohneFirma ? 'warn' : 'neutral', to: '/companies', hint: ohneFirma && darfImportieren ? 'unter Firmen „Leads zuordnen“' : undefined },
          ]}
        />
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <div className="tabs" role="tablist" aria-label="Bereich">
        {bereiche.map((b) => (
          <button key={b.id} type="button" role="tab" aria-selected={bereich === b.id} className={`tab ${bereich === b.id ? 'tab-active' : ''}`} onClick={() => set('tab', b.id === 'leads' ? null : b.id, { replace: true })}>
            {b.label}
          </button>
        ))}
      </div>

      {leads.length === 0 && !gefiltert ? (
        <EmptyState
          icon="users"
          title="Noch keine Leads"
          description="Leads kommen per CSV aus Excel oder einem LinkedIn-Export, von Hand, oder aus einer Vertriebskampagne. Danach: mit Clay anreichern und in eine Lemlist-Kampagne aufnehmen."
          action={darfImportieren
            ? { label: 'Importieren', onClick: () => setZeigeImport(true) }
            : darfBearbeiten ? { label: 'Neuer Lead', onClick: () => { setBearbeite(null); setZeigeFormular(true); } } : undefined}
          secondaryAction={darfBearbeiten ? { label: 'Aus Kampagne übernehmen', to: '/sales-campaigns' } : undefined}
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="toolbar__group">
              <input
                type="search"
                placeholder="Name, E-Mail, Firma…"
                aria-label="Leads durchsuchen"
                value={sucheEingabe}
                onChange={(e) => setSucheEingabe(e.target.value)}
              />
              <select aria-label="Status" value={status} onChange={(e) => set('status', e.target.value, { replace: true })}>
                <option value="">Alle Status</option>
                {Object.entries(LEAD_STATUS).map(([wert, s]) => <option key={wert} value={wert}>{s.label}</option>)}
              </select>
              <select aria-label="Quelle" value={quelle} onChange={(e) => set('quelle', e.target.value, { replace: true })}>
                <option value="">Alle Quellen</option>
                {Object.entries(LEAD_QUELLE).map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
              </select>
              {kampagne && (
                <span className="lead-kampagnen-filter">
                  Kampagne: <strong>{kampagnenName(kampagne)}</strong>
                  <KampagneVerweis lemlistId={kampagne} zuordnung={zuordnung} />
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => set('kampagne', null, { replace: true })} aria-label="Kampagnenfilter entfernen">
                    <Icon name="close" size={12} />
                  </button>
                </span>
              )}
              {gefiltert && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={filterZuruecksetzen}>
                  <Icon name="close" size={12} /> Filter zurücksetzen
                </button>
              )}
            </div>
            <div className="toolbar__group toolbar__group--right lead-auswahl" aria-live="polite">
              <span className="text-sm">{ausgewaehlt.size ? <strong>{ausgewaehlt.size} ausgewählt</strong> : `${sichtbar.length} Leads`}</span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={alleUmschalten}>
                {sichtbar.length > 0 && sichtbar.every((l) => ausgewaehlt.has(l.id)) ? 'keine' : 'alle sichtbaren'}
              </button>
            </div>
          </div>

          {bereich === 'leads' && (
            sichtbar.length === 0 ? (
              <EmptyState compact icon="search" title="Kein Lead passt zum Filter" action={{ label: 'Filter zurücksetzen', onClick: filterZuruecksetzen }} />
            ) : (
              <LeadTabelle
                leads={sichtbar}
                ausgewaehlt={ausgewaehlt}
                onToggle={umschalten}
                onToggleAlle={alleUmschalten}
                onBearbeiten={darfBearbeiten ? (l) => { setBearbeite(l); setZeigeFormular(true); } : undefined}
                onEntfernen={darfSenden ? ausKampagne : undefined}
                onLoeschen={darf('vertrieb.loeschen') ? loeschen : undefined}
                kampagnenName={kampagnenName}
              />
            )
          )}
          {bereich === 'clay' && <AnreicherungPanel leads={leads} ausgewaehlt={ausgewaehltIds} onGeladen={laden} onFehler={setFehler} />}
          {bereich === 'icebreaker' && <IcebreakerPanel leads={leads} ausgewaehlt={ausgewaehltIds} onGeladen={laden} onFehler={setFehler} />}
          {bereich === 'lemlist' && (
            <KampagnenPanel
              leads={leads}
              ausgewaehlt={ausgewaehltIds}
              kampagnen={kampagnen}
              laedtKampagnen={laedtKampagnen}
              onKampagnenLaden={kampagnenLaden}
              onGeladen={laden}
              onFehler={setFehler}
              darfSenden={darfSenden}
              onEntfernen={darfSenden ? ausKampagne : undefined}
            />
          )}
          {bereich === 'durchlauf' && (
            <DurchlaufPanel
              ausgewaehlt={ausgewaehltIds}
              kampagnen={kampagnen}
              laedtKampagnen={laedtKampagnen}
              onKampagnenLaden={kampagnenLaden}
              onGeladen={laden}
              onFehler={setFehler}
            />
          )}
        </>
      )}

      {darfBearbeiten && (
        <LeadFormular
          open={zeigeFormular}
          lead={bearbeite}
          onClose={() => setZeigeFormular(false)}
          onSaved={async () => { setZeigeFormular(false); await laden(); }}
        />
      )}
      {darfImportieren && <ImportDialog open={zeigeImport} onClose={() => setZeigeImport(false)} onImported={() => { laden(); }} />}
    </div>
  );
};

export default LeadSyncPage;
